import React, { Dispatch, SetStateAction, useMemo } from "react";
import { Form, Input } from "domains/3d/interfaces";
import { getStepSizeForRange } from "domains/3d/utils";
import { IS_PROD } from "domains/commons/environment";
import { disabledOnWheel } from "domains/forms/disabledOnWheel";
import ButtonSectionTopIcon from "domains/inference/components/InferenceGenerator/Sidebar/ButtonSectionTopIcon";
import FragmentReferenceImages from "domains/inference/components/InferenceGenerator/Sidebar/FragmentReferenceImages";
import SidebarSection from "domains/inference/components/InferenceGenerator/Sidebar/Section";
import SectionPrompt from "domains/inference/components/InferenceGenerator/Sidebar/SectionPrompt";
import SectionReferenceImageBase from "domains/inference/components/InferenceGenerator/Sidebar/SectionReferenceImageBase";
import { ReferenceAsset } from "domains/inference/interfaces/InferenceGenerator";
import ControlSwitch from "domains/ui/components/ControlSwitch";
import Icon from "domains/ui/components/Icon";
import ScenarioInput from "domains/ui/components/ScenarioInput";
import Select from "domains/ui/components/Select";
import Slider from "domains/ui/components/Slider";
import WithLabelAndTooltip from "domains/ui/components/WithLabelAndTooltip";
import { GetModelsByModelIdApiResponse } from "infra/api/generated/api";

import { Text, VStack } from "@chakra-ui/react";

export interface GenericInputProps {
  input: Input;
  model: GetModelsByModelIdApiResponse["model"] | undefined;
  form: Form;
  setForm: Dispatch<SetStateAction<Form>>;
  type: "3d" | "video";
}

export default function GenericInput({
  input,
  model,
  form,
  setForm,
  type,
}: GenericInputProps) {
  const value = useMemo(() => form[input.name], [form, input.name]);
  const label = input.label ?? input.name;

  const isPrompt = input.type === "string" && typeof value === "string";

  const firstAssetId = useMemo(() => {
    if (isPrompt && type === "video") {
      return Object.values(form).find(
        (v) => typeof v === "object" && "url" in v && "id" in v
      )?.id;
    }
    return undefined;
  }, [isPrompt, form, type]);

  const allImages: ReferenceAsset[] | undefined = useMemo(() => {
    if (isPrompt && type === "video") {
      const images = Object.values(form).reduce<ReferenceAsset[]>((acc, v) => {
        if (typeof v === "object" && "url" in v && "id" in v) {
          acc.push(v);
        } else if (
          Array.isArray(v) &&
          v.every((v) => typeof v === "object" && "url" in v && "id" in v)
        ) {
          acc.push(...v);
        }
        return acc;
      }, [] as ReferenceAsset[]);
      return images.length > 0 ? images : undefined;
    }
    return undefined;
  }, [isPrompt, form, type]);

  if (
    ((input.type === "string" && typeof value === "string") ||
      (input.type === "number" && typeof value === "number")) &&
    input.allowedValues
  ) {
    return (
      <WithLabelAndTooltip label={label} tooltip={input.description}>
        <Select
          w="fit-content"
          mr={-1}
          ml="auto"
          onChange={(e) =>
            setForm((form) => ({
              ...form,
              [input.name]:
                input.type === "string"
                  ? e.target.value
                  : Number(e.target.value),
            }))
          }
          value={value}
        >
          {input.allowedValues.map((allowedValue) => (
            <option
              key={allowedValue}
              value={allowedValue}
              label={allowedValue.toString()}
            />
          ))}
        </Select>
      </WithLabelAndTooltip>
    );
  } else if (
    input.type === "number" &&
    typeof value === "number" &&
    input.min !== undefined &&
    input.max !== undefined &&
    input.name !== "seed" // special case for seed, we want a text input
  ) {
    const step = getStepSizeForRange(input.min, input.max);
    return (
      <WithLabelAndTooltip label={label} tooltip={input.description}>
        <Slider
          withNumberInput
          data-testid={`inference-builder-${input.name}`}
          max={input.max}
          min={input.min}
          onChange={(value) =>
            setForm((form) => ({ ...form, [input.name]: value }))
          }
          step={step}
          value={value}
        />
      </WithLabelAndTooltip>
    );
  } else if (input.prompt && typeof value === "string") {
    return (
      <SectionPrompt
        id={input.name}
        prompt={value ?? ""}
        setPrompt={(prompt) =>
          setForm((form) => ({ ...form, [input.name]: prompt }))
        }
        tooltip={input.description}
        title={label}
        promptReferenceAssetId={firstAssetId}
        getModel={async () => model}
        maxLength={input.maxLength}
        {...(type === "video" && {
          rewriteMode: "contextual",
          promptSparkImages: allImages,
        })}
      />
    );
  } else if (
    (input.type === "string" && typeof value === "string") ||
    (input.type === "number" &&
      (typeof value === "number" || value === undefined))
  ) {
    return (
      <WithLabelAndTooltip label={label} tooltip={input.description}>
        <ScenarioInput
          value={value === undefined ? "" : value.toString()}
          setValue={(value) =>
            setForm((form) => ({
              ...form,
              [input.name]:
                input.type === "string"
                  ? value
                  : value === ""
                  ? undefined
                  : Number(value),
            }))
          }
          type={input.type === "string" ? "text" : "number"}
          onWheel={disabledOnWheel}
          dataTestId={`inference-builder-${input.name}`}
        />
      </WithLabelAndTooltip>
    );
  } else if (input.type === "boolean" && typeof value === "boolean") {
    return (
      <ControlSwitch
        title={label}
        tooltip={input.description}
        isChecked={value}
        setIsChecked={(value) =>
          setForm((form) => ({ ...form, [input.name]: value }))
        }
      />
    );
  } else if (
    input.type === "file" &&
    ((typeof value === "object" && "url" in value && "id" in value) ||
      value === undefined)
  ) {
    const kind = ["image", "video", "3d"].includes(input.kind ?? "")
      ? (input.kind as "image" | "video" | "3d")
      : "image";
    return (
      <SectionReferenceImageBase
        id={input.name}
        title={label}
        tooltip={input.description}
        asset={value}
        setAsset={(asset) => {
          setForm((form) => ({
            ...form,
            [input.name]: asset,
          }));
        }}
        withRemoveBackground
        removeBackgroundTrackingExtraParams={{
          origin: "custom page",
        }}
        withSketch="never"
        selectLibrary={{
          tabs:
            type === "video" && kind === "image" ? ["image", "skybox"] : [kind],
        }}
        kind={kind}
        isPasteEnabled={input.isWithPasteImage}
        recommendedBackgroundTransparency={input.backgroundBehavior}
      />
    );
  } else if (
    input.type === "file_array" &&
    Array.isArray(value) &&
    value.every(
      (v) =>
        typeof v === "object" && (("url" in v && "id" in v) || "isLoading" in v)
    )
  ) {
    const kind = ["image", "video", "3d"].includes(input.kind ?? "")
      ? (input.kind as "image" | "video" | "3d")
      : "image";
    return (
      <SidebarSection
        headerProps={{
          title: label,
          id: input.name,
          tooltip: input.description,
          topRight:
            value.length > 0 ? (
              <ButtonSectionTopIcon
                tooltip="Remove All"
                tooltipProps={{ placement: "right" }}
                onClick={() =>
                  setForm((form) => ({ ...form, [input.name]: [] }))
                }
              >
                <Icon id="Ui/TrashMd" h="14px" />
              </ButtonSectionTopIcon>
            ) : undefined,
        }}
      >
        <VStack align="stretch" w="100%" spacing={2}>
          <FragmentReferenceImages
            assets={value}
            setAssets={(newValue) =>
              setForm((form) => ({ ...form, [input.name]: newValue }))
            }
            maxImages={input.maxLength ?? 10}
            removeBackgroundTrackingExtraParams={{
              origin: "custom page",
            }}
            isPasteEnabled={input.isWithPasteImage}
            recommendedBackgroundTransparency={input.backgroundBehavior}
            selectLibrary={{
              tabs:
                type === "video" && kind === "image"
                  ? ["image", "skybox"]
                  : [kind],
            }}
            kind={kind}
          />
        </VStack>
      </SidebarSection>
    );
  }

  if (IS_PROD) {
    return null;
  }

  return <Text>{input.type}</Text>;
}
